<template>
    <div class="section2">
        <div class="section bordered">
            <h2>User Information</h2>
            <div class="content" data-section-name="userinfo">
                <b-row v-if="isEdit" class="mt-4">
                    <b-col lg="4">
                        <label class="thick">Email<span class="arm-required">*</span></label>
                    </b-col>
                    <b-col lg="4">
                        <b-form-input
                          v-model.trim="people.email"
                          class="rounded-0"
                          :disabled="isEdit"
                          type="email"
                          placeholder="Please enter your email"
                          aria-describedby="input-live-help email-feedback"
                        />
                    </b-col>
                </b-row>
              <b-row
                  class="mt-5"
                  align-v="center"
              >
                <b-col lg="4">
                  <label class="thick">
                    Link ORCID record
                  <span class="arm-required">*</span>
                  </label>
                  <p>
                    <a
                        href="https://orcid.org/"
                        target="_blank"
                    >
                      ORCID
                    </a>
                    provides a persistent digital identifier that distinguishes you from other researchers.
                    <strong>Linking your ORCID is required to create an ARM account.</strong></p>
                </b-col>
                <b-col
                  v-if="orcidIsPresent && orcidValidated"
                  lg="6"
                >
                  <OrcidLink :orcid="orcid" />
                  <b-icon
                    icon="check"
                    variant="success"
                    font-scale="2"
                    class="ml-1 align-middle"
                  />
                </b-col>

                <b-col
                  v-else-if="orcidIsPresent"
                  lg="6"
                >
                <div v-if="$v.people.orcid.$error && isRecordSubmitted" class="text-danger mb-2">
                  ORCID is required
                </div>
                  <b-input
                    :value="orcid"
                    disabled
                  />
                  <b-button
                      class="orcid-button ml-0 mr-0 mt-2 mb-0"
                      variant="arm-primary"
                      block
                      @click="connectToOrcid"
                  >
                    <img
                        alt="ORCID logo"
                        src="../assets/ORCIDiD_iconvector.svg"
                        width="24"
                        height="24"
                    />
                    Create or connect your ORCID
                  </b-button>
                </b-col>
                <b-col
                  v-else
                  lg="6"
                >
                  <b-button
                    class="orcid-button m-0"
                    variant="arm-primary"
                    block
                    @click="connectToOrcid"
                  >
                    <img
                      alt="ORCID logo"
                      src="../assets/ORCIDiD_iconvector.svg"
                      width="24"
                      height="24"
                    />
                    Create or connect your ORCID
                  </b-button>
              </b-col>
              </b-row>
                <b-row class="mt-4">
                    <b-col lg="4">
                        <label class="thick" for="firstName">First name <span class="arm-required">*</span></label>
                        <b-form-input class="rounded-0" v-model.trim="people.nameFirst" name="people.firstName" placeholder="First name ..." :state="validateState('nameFirst')" aria-describedby="feedback"></b-form-input>
                        <b-form-invalid-feedback
                          id="feedback"
                          class="thick"
                        >
                          First Name is required and must not include invalid characters such as {{ restrictedCharacters }}
                        </b-form-invalid-feedback>
                        <div
                          v-if="validateState('nameFirst') && people.nameFirst.length === 1"
                          class="font-weight-bold"
                        >
                          Note: Please ensure you only intended to have a single character as your first name
                        </div>
                    </b-col>
                    <b-col lg="4">
                        <label class="thick" for="lastName">Last name <span class="arm-required">*</span></label>
                        <b-form-input v-model.trim="people.nameLast" class="rounded-0" name="people.lastName" placeholder="Last name ..." :state="validateState('nameLast')" aria-describedby="feedback"></b-form-input>
                        <b-form-invalid-feedback
                          id="feedback"
                          class="thick"
                        >
                          Last Name is required and must not include invalid characters such as {{ restrictedCharacters }}
                        </b-form-invalid-feedback>
                        <div
                          v-if="validateState('nameLast') && people.nameLast.length === 1"
                          class="font-weight-bold"
                        >
                          Note: Please ensure you only intended to have a single character as your last name
                        </div>
                    </b-col>
                    <b-col lg="4">
                        <label for="middleName">Middle name</label>
                        <b-form-input
                          v-model.trim="people.nameMid"
                          class="rounded-0"
                          name="people.middleName"
                          placeholder="Middle name ..."
                          :state="validateState('nameMid')"
                          aria-describedby="feedback"
                        />
                        <b-form-invalid-feedback
                          id="feedback"
                          class="thick"
                        >
                          Middle Name must not include invalid characters such as {{ restrictedCharacters }}
                        </b-form-invalid-feedback>
                    </b-col>
                </b-row>
                <b-row class="mt-3">

                  <b-col lg="4">
                        <label
                          class="thick"
                          for="street"
                        >
                          Secondary email
                        </label>
                        <b-form-input
                          v-model.trim="people.secondaryEmail"
                          class="rounded-0"
                          name="people.secondaryEmail"
                          placeholder="Secondary email ..."
                          :state="validateState('secondaryEmail')"
                          aria-describedby="feedback"
                        />
                        <b-form-invalid-feedback
                          id="feedback"
                          class="thick"
                        >
                          Secondary email must conform to a valid email address format
                        </b-form-invalid-feedback>
                    </b-col>

                    <b-col lg="4">
                        <label class="thick" for="phone">Phone <span class="arm-required">*</span></label>
                        <b-form-input
                          v-model.trim="people.phone"
                          class="rounded-0"
                          name="people.phone"
                          placeholder="Phone ..."
                          :state="validateState('phone')"
                          aria-describedby="feedback"
                        />
                        <b-form-invalid-feedback
                          id="feedback"
                          class="thick"
                          v-if="!$v.people.phone.required"
                        >
                          Phone is required
                        </b-form-invalid-feedback>
                        <b-form-invalid-feedback
                          id="feedback"
                          class="thick"
                          v-if="!$v.people.phone.maxLength"
                        >
                          Phone can have at most
                          {{$v.people.phone.$params.maxLength.max}}
                          digits
                        </b-form-invalid-feedback>
                        <b-form-invalid-feedback
                          id="feedback"
                          class="thick"
                        >
                          Phone must not include invalid characters such as {{ restrictedCharacters }}
                        </b-form-invalid-feedback>
                    </b-col>
                    <b-col lg="4">
                        <label class="thick">Citizenship <span class="arm-required">*</span></label>
                        <b-form-select
                          v-model.trim="people.citizenCountryCode"
                          class="rounded-0"
                          name="people.citizenCountryCode"
                          :options="countries"
                          :state="validateState('citizenCountryCode')"
                          aria-describedby="feedback"
                        />
                        <b-form-invalid-feedback
                          id="feedback"
                          class="thick"
                        >
                          Citizen status is required
                        </b-form-invalid-feedback>
                    </b-col>
                </b-row>
              <DemographicsDescriptionSection />
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState } from "vuex"
    import { validationMixin } from "vuelidate";
    import { required, requiredIf, maxLength } from "vuelidate/lib/validators";
    import Config from "@/config"
    import DemographicsDescriptionSection from "@/components/DemographicsDescriptionSection"
    import OrcidLink from "@/components/OrcidLink"

    const oneOrMoreValidator = Config.validations.oneOrMoreValidator
    const zeroOrMoreValidator = Config.validations.zeroOrMoreValidator
    const zeroOrMoreSlashValidator = Config.validations.zeroOrMoreSlashValidator
    const emailValidator = Config.validations.emailValidator
    const nameValidator = Config.validations.nameValidator

    export default {
        name: "UserSectionTemplate",
        mixins: [validationMixin],
        components: {
          OrcidLink,
          DemographicsDescriptionSection,
        },
        props: {
            people: {default: null, type: Object},
            isRecordSubmitted: {default: false, type: Boolean},
            isEdit: {default: false, type: Boolean},
            orcidInfo: {required: false, default: ""},
        },
        data() {
            return {
                isEmailUpdated: false,
            }
        },
        computed: {
            ...mapState("app", [
              "countries",
            ]),
            restrictedCharacters() {
              return Config.restrictedCharacters
            },
            orcidIsPresent() {
              return (this.orcidInfo && Object.keys(this.orcidInfo).length > 0) || this.people.orcid
            },
            orcidValidated() {
              return this.people.orcidValidation
            },
            orcid() {
              return this.orcidInfo?.["orcid-identifier"]?.path || this.people.orcid
            },
            restrictedSlashCharacters() {
              return Config.restrictedSlashCharacters
            },
            validationObject() {
              return JSON.stringify(this.$v.people)
            },
        },
        validations: {
            people: {
                email: {
                  required,
                  emailValidator,
                },
                secondaryEmail: {
                  emailValidator,
                },
                nameFirst: {
                    required,
                    nameValidator,
                },
                nameMid: {
                  zeroOrMoreValidator,
                },
                nameLast: {
                    required,
                  oneOrMoreValidator,
                },
                phone: {
                    required,
                    maxLength: maxLength(16),
                    oneOrMoreValidator,
                },
                citizenCountryCode: {
                    required,
                    oneOrMoreValidator,
                },
                orcid : {
                  required
                }
            }
        },
        created() {
            this.$store.dispatch("app/getCountries")
        },
        watch: {
            isRecordSubmitted() {
                this.$v.people.$touch();
            },
            validationObject: {
              handler(newVal, oldVal) {
                const a = JSON.parse(newVal)
                const b = JSON.parse(oldVal)
                Object.keys(a.$params).forEach(key => {
                  if (a[key].$model !== b[key].$model) {
                    this.$v.people[key].$touch()
                    this.$emit("userSectionValid", !this.$v.people.$invalid)
                  }
                })
              },
              deep: true,
            },
            "people.secondaryEmail":{
              handler(value){
                // remove secondaryEmail key from people object if the user attempts to fill the field but clears it out
                if (!value && this.people.hasOwnProperty('secondaryEmail')){
                  delete this.people.secondaryEmail
                }
              },
              deep: true,
            },
            "people.countryCode"(newVal){
              if (newVal !== "US") {
                this.people.state = null
              }
            }
        },
        methods: {
          validateStateCode(val) {
            // Default the state code to None if the country is not US
            this.people.state = val && val != "US" ? null : this.people.state
          },
            validateState(name) {
                var { $dirty, $error } = this.$v.people[name];
                if (name === "state"){
                  $error = !this.people.state || this.people.state === "0";
                }
                return $dirty ? !$error : null;
            },
            validateEmail () {
                this.isEmailUpdated = true
                if (this.people.email)
                    this.$store.dispatch("profile/validateEmail", this.people.email)
            },
            connectToOrcid() {
              this.$emit("persistUserInfo")
              const url = new URL(Config.orcid.authenticateUrl)

              const redirectUri = this.isEdit && this.people.username ?
                `${Config.orcid.redirectUri}/edit/${this.people.username}` :
                `${Config.orcid.redirectUri}/new`

              url.searchParams.append("client_id", Config.orcid.clientId)
              url.searchParams.append("response_type", Config.orcid.responseType)
              url.searchParams.append("scope", Config.orcid.scope)
              url.searchParams.append("redirect_uri", redirectUri)
              url.searchParams.append("nonce", Config.orcid.nonce)
              window.open(url.toString(), "_self")
            }
        }
    }
</script>

<style scoped>
</style>
